import { ParentState, setContextWithKey } from "@hopper-b2b/checkout";
import { ClientName } from "@hopper-b2b/types";
import { parsePhoneNumber } from "@hopper-b2b/utilities";
import type { UserInfo } from "@hsbc/auth";
import { branding } from "@hsbc/portal";
import { assign } from "xstate";

// Tenants where contact info should NOT be auto-populated from user profile.
// HSBC-1285: HSBC-IN disabled due to high volume of customer requests to update
// profile contact info, potentially fraud-related.
const CONTACT_AUTOCOMPLETE_DISABLED_CLIENTS: ClientName[] = [
  ClientName.HSBC_IN,
];
const CLIENT_NAME_BY_CURRENCY: Partial<Record<string, ClientName>> = {
  INR: ClientName.HSBC_IN,
  SGD: ClientName.HSBC,
};
type CheckoutUserInfo = UserInfo & { defaultCurrency?: string };

const getClientName = ({
  clientName,
  currency,
}: {
  clientName?: ClientName;
  currency?: string;
}): ClientName =>
  clientName || CLIENT_NAME_BY_CURRENCY[currency || ""] || ClientName.HSBC;

export const getInitialContactInformation = ({
  userInfo,
  clientName,
  defaultAreaCode,
}: {
  userInfo: CheckoutUserInfo;
  clientName?: ClientName;
  defaultAreaCode: string;
}) => {
  const resolvedClientName = getClientName({
    clientName,
    currency: userInfo.defaultCurrency,
  });
  const isAutocompleteDisabled =
    CONTACT_AUTOCOMPLETE_DISABLED_CLIENTS.includes(resolvedClientName);
  const parsedPhone = parsePhoneNumber(userInfo.phoneNumber);

  return {
    countryCode: isAutocompleteDisabled
      ? defaultAreaCode
      : parsedPhone.countryCode
        ? `+${parsedPhone.countryCode}`
        : defaultAreaCode,
    phoneNumber: isAutocompleteDisabled ? "" : parsedPhone.nationalNumber,
    email: isAutocompleteDisabled ? "" : userInfo.email || "",
  };
};

export const commonCheckoutActions = {
  setInitialContactInformation: assign(
    <T extends { sessionInfo: { userInfo: UserInfo } }>(context: T) => {
      const userInfo = context.sessionInfo.userInfo as CheckoutUserInfo;
      const defaultAreaCode =
        (branding?.default &&
          branding.translation?.[branding.default]?.preferredAreaCode) ||
        "+65";
      const initialContactInfo = getInitialContactInformation({
        userInfo,
        clientName: branding.clientName,
        defaultAreaCode,
      });

      return setContextWithKey(
        context,
        `${ParentState.contactInformation}.contactInfo`,
        initialContactInfo
      );
    }
  ),
};
